import {
  SaveDesignToProfileModalEnum,
  SaveDesignToProfileToastMessage
} from '@inter-ikea-kompis/component-save-design-to-profile';
import { ToastManager } from '@inter-ikea-kompis/component-toast';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { IConfigurationOptions } from '@inter-ikea-kompis/types';
import { HelpLinesComponentVisible, HelpLinesSystem } from '@inter-ikea-storageone/range';
import {
  reportAccountLogin,
  reportSaveDesign,
  reportSaveDesignClick,
  ReportSourceEnum
} from 'analytics/reporters';
import { useCore } from 'contexts/core';
import { useScreenshot } from 'contexts/screenshot';
import { useServices } from 'contexts/services';
import { useServiceSettings } from 'contexts/serviceSettings';
import { getProductEntities } from 'ducks/core';
import { saveDesignId } from 'ducks/designId';
import { useAppDispatch } from 'ducks/hooks';
import {
  getAgeGroup,
  LastSavedConfiguration,
  lastSavedConfiguration,
  setLastSavedConfiguration
} from 'ducks/layout';
import { getShoppingItemsInScene } from 'ducks/products';
import { hideSaveDesignToProfileSheet } from 'ducks/sheets';
import { getVpcCode } from 'ducks/vpc';
import reportError from 'error-reporting/reportError';
import useFetchShoppingProducts from 'hooks/useFetchShoppingProducts';
import { useTranslations } from 'hooks/useTranslations';
import { equals } from 'ramda';
import { getFramesInScene } from 'rangeConfig/shared/utils/colorUtils/utils';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { createDesignConfiguration } from 'utils/designConfiguration';
import { isProduction } from 'utils/platform';
import { getRange } from 'utils/queryUtils';
import { getStringMeasurements, wardrobeTextTranslation } from 'views/Summary/utils';

export const getMappedConfiguration = (
  configuration: any,
  prevSavedConfiguration: LastSavedConfiguration
): LastSavedConfiguration | undefined => {
  const mappedConfiguration = (configuration.entities as any)
    .map((e: any) => ({
      id: e.id,
      ref: e.ref,
      position: e.c.WorldTransformComponent
    }))
    .filter((e: any) => e.position);

  if ((configuration.entities as any)?.length - 1 !== prevSavedConfiguration?.length) {
    return mappedConfiguration;
  }

  // If the configuration is the same as the last one saved we don't need to save it again.
  // We show the toast saying we saved anyways because it will not matter to the user if we
  // actually saved or not.
  if (equals(prevSavedConfiguration, mappedConfiguration)) {
    return undefined;
  }

  return mappedConfiguration;
};

type Args = {
  setVisibleModal: React.Dispatch<React.SetStateAction<SaveDesignToProfileModalEnum | null>>;
};

export const useSaveDesign = (
  { setVisibleModal }: Args = {
    setVisibleModal: () => {}
  }
) => {
  const { rangeName, kompisSettings, translations } = useServiceSettings();
  const { accountService, saveService } = useServices();
  const dispatch = useAppDispatch();
  const coreApi = useCore();
  const { formatMessage } = useIntl();
  const { useMetric } = useTranslations();
  const range = getRange();

  const productsInScene = useSelector(getShoppingItemsInScene);
  const vpcCode = useSelector(getVpcCode);
  const productEntitiesInScene = useSelector(getProductEntities);
  const { shoppingItems } = useFetchShoppingProducts(productsInScene);
  const currentProppingAgeGroup = useSelector(getAgeGroup);
  const prevSavedConfiguration = useSelector(lastSavedConfiguration);

  const { captureVPCImages } = useScreenshot();

  const initiateLogin = async (
    source: ReportSourceEnum,
    loginOptions?: {
      state: string;
    }
  ) => {
    reportAccountLogin({ source });
    accountService().logIn(loginOptions);
  };

  const saveToProfile = async () => {
    const vpcImages = await captureVPCImages();
    const helpLinesSystem = coreApi.getSystem<HelpLinesSystem>('HelpLinesSystem');
    const scene = coreApi.entities.getScene();
    const helpLinesVisible = scene?.getComponent<HelpLinesComponentVisible>(
      HelpLinesComponentVisible.id
    );

    const wallShape = helpLinesVisible
      ? {
          dimensions: helpLinesSystem?.getShapeDimensions(),
          type: helpLinesSystem?.currentShapeType
        }
      : undefined;

    const outerMeasurements = coreApi.entities.getOuterMeasurements();
    const measurements = outerMeasurements
      ? `
      ${formatMessage({ id: wardrobeTextTranslation() })},
      ${getStringMeasurements(outerMeasurements, translations, useMetric)}
      ${formatMessage({ id: 'kompis.measureValue' })}
    `
      : '';
    const frameEntities = getFramesInScene(coreApi);
    const vpcAltText =
      range.toUpperCase() +
      ' ' +
      formatMessage({ id: 'storageone.storageSolution' }).toLowerCase() +
      ', ' +
      frameEntities.length +
      ' ' +
      formatMessage({ id: 'storageone.productListFramesTitle' }).toLowerCase();

    const insightsCombinationFormat = coreApi.getInsightsCombinationFormat(
      productsInScene,
      isProduction(),
      kompisSettings.dexfApplicationId
    );
    const toastManager = new ToastManager({ theme: SkapaTheme });
    reportSaveDesignClick({ source: ReportSourceEnum.saveDesignSheet });

    if (productEntitiesInScene.length > 0) {
      const configuration = createDesignConfiguration(coreApi, currentProppingAgeGroup, wallShape);

      const mappedConfiguration = getMappedConfiguration(configuration, prevSavedConfiguration);

      if (!mappedConfiguration) {
        toastManager.showToast(
          SaveDesignToProfileToastMessage.getSaveDesignSuccess(translations.kompis)
        );
        dispatch(hideSaveDesignToProfileSheet());
        return;
      }

      // We save the current shopping items so we can see next time the user hits save if
      // there is a change in the shopping list.
      dispatch(setLastSavedConfiguration(mappedConfiguration));

      if (configuration) {
        const configurationOptions: IConfigurationOptions = {
          // The description will be displayed and used to describe the saved design in e.g. component responsible for listing and opening designs.
          configurationDescription: measurements,
          configurationName: range.toUpperCase(),
          versionInfo: vpcAltText,
          configuration: configuration,
          shoppingItems: shoppingItems,
          configurationImage: vpcImages,
          insightsCombinationFormat
        };

        try {
          const user = await accountService().getUser();

          if (!user) {
            // This likely means the token has expired. Prompt the user to log in again.
            initiateLogin(ReportSourceEnum.saveDesignSheet, { state: 'saveDesign' });
            return;
          }

          const savedConfiguration = await saveService.saveConfiguration({
            token: user.accessToken,
            configurationOptions
          });

          setVisibleModal(null);

          if (savedConfiguration) {
            reportSaveDesign({
              designId: savedConfiguration.designId,
              vpcCode: vpcCode ? vpcCode : '',
              page: ReportSourceEnum.saveDesignSheet
            });

            if (!savedConfiguration.icfSaved) {
              reportError(new Error('Insights Combination Format was not saved successfully.'), {
                designId: savedConfiguration.designId,
                vpcCode: vpcCode
              });
            }

            toastManager.showToast({
              label: formatMessage(
                { id: 'storageone.saveDesignToastMessage' },
                { range: rangeName.toUpperCase() }
              )
            });

            dispatch(saveDesignId(savedConfiguration.designId));
            dispatch(hideSaveDesignToProfileSheet());
          } else {
            toastManager.showToast(
              SaveDesignToProfileToastMessage.getSaveDesignFailure(translations.kompis)
            );
          }
        } catch (error) {
          setVisibleModal(null);
          dispatch(hideSaveDesignToProfileSheet());
          toastManager.showToast(
            SaveDesignToProfileToastMessage.getSaveDesignFailure(translations.kompis)
          );
        }
      }
    }
  };

  return { saveToProfile, initiateLogin };
};
